import React, { useMemo, useState } from 'react';
import {
  Box,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Control, Controller, FieldErrors, useForm } from 'react-hook-form';
import { RepairFormulaDto } from '@vatos-pas/common';

import {
  Autocomplete,
  BooleanInput,
  FormToolbar,
  SnackbarContainer,
} from 'components/standalone';
import { createValidationResolver } from 'utils';
import {
  RepairFormulaFormValues,
  RepairOption,
} from '../containers';
import { validateRepairFormula } from '../views/repairsSettingsValidation';
import {
  BumperPaymentType,
  RepairPaymentType,
  RepairResourceType,
} from '../views/constants';

export interface RepairsSettingsFormProps {
  mode: 'create' | 'edit';
  initialValues?: Partial<RepairFormulaDto>;
  onSubmit: (values: RepairFormulaFormValues) => Promise<void>;
  loading: boolean;
  onGoBack: () => void;
  loadRepairOptions: (searchTerm: string) => Promise<RepairOption[]>;
  selectedRepairOption?: RepairOption | null;
}

type SelectFieldName = Extract<
  keyof RepairFormulaFormValues,
  'repairResourceType' | 'repairBumperPaymentType' | 'repairPaymentType'
>;

interface SelectOption {
  id: string;
  name: string;
}

interface SelectFieldProps {
  name: SelectFieldName;
  label: string;
  options: SelectOption[];
  control: Control<RepairFormulaFormValues>;
  errors: FieldErrors<RepairFormulaFormValues>;
  disabled: boolean;
}

function SelectField({
  name,
  label,
  options,
  control,
  errors,
  disabled,
}: SelectFieldProps): JSX.Element {
  const fieldError = errors[name];
  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => (
        <FormControl
          fullWidth
          variant="filled"
          margin="dense"
          error={!!fieldError}
        >
          <InputLabel>{label}</InputLabel>
          <Select {...field} disabled={disabled}>
            {options.map(option => (
              <MenuItem key={option.id} value={option.id}>
                {option.name}
              </MenuItem>
            ))}
          </Select>
          {fieldError && <FormHelperText>{fieldError.message}</FormHelperText>}
        </FormControl>
      )}
    />
  );
}

const getDefaultValues = (
  initialValues?: Partial<RepairFormulaDto>,
): RepairFormulaFormValues => ({
  repairId: initialValues?.repairId || '',
  repairResourceType: initialValues?.repairResourceType || '',
  repairBumperPaymentType: initialValues?.repairBumperPaymentType || '',
  repairPaymentType: initialValues?.repairPaymentType || '',
  active: initialValues?.active ?? true,
});

export const RepairsSettingsForm: React.FC<RepairsSettingsFormProps> = ({
  mode,
  initialValues,
  onSubmit,
  loading,
  onGoBack,
  loadRepairOptions,
  selectedRepairOption = null,
}) => {
  const classes = useStyles();
  const isEdit = mode === 'edit';
  const { notify } = SnackbarContainer.useContainer();

  const formValues = useMemo(
    () => (initialValues ? getDefaultValues(initialValues) : undefined),
    [initialValues],
  );

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<RepairFormulaFormValues>({
    defaultValues: getDefaultValues(initialValues),
    values: formValues,
    resetOptions: { keepDirtyValues: true },
    resolver: createValidationResolver<RepairFormulaFormValues>(
      validateRepairFormula,
    ),
    mode: 'onBlur',
  });

  const [createSelectedRepair, setCreateSelectedRepair] =
    useState<RepairOption | null>(null);

  const displayedRepairOption = isEdit
    ? selectedRepairOption
    : createSelectedRepair;

  const onFormSubmit = useMemo(
    () =>
      handleSubmit(
        async data => {
          await onSubmit(data);
        },
        () => {
          notify('The form is not valid. Please check for errors', 'error');
        },
      ),
    [handleSubmit, onSubmit, notify],
  );

  return (
    <Box>
      <Box className={classes.formContent}>
        <Box className={classes.fields}>
          <Controller
            name="repairId"
            control={control}
            render={({ field: { onChange } }) => (
              <Autocomplete<RepairOption>
                label="Repair Type"
                value={displayedRepairOption}
                onChange={option => {
                  setCreateSelectedRepair(option);
                  onChange(option?.id ?? '');
                }}
                loadOptions={loadRepairOptions}
                loadTrigger="mount"
                clientSideFilter
                getOptionLabel={option => option?.name ?? ''}
                getOptionValue={option => option?.id ?? ''}
                error={errors.repairId?.message}
                disabled={isEdit}
                fullWidth
              />
            )}
          />
          <SelectField
            name="repairResourceType"
            label="Resource Type"
            options={RepairResourceType}
            control={control}
            errors={errors}
            disabled={isEdit}
          />
        </Box>

        <Box className={classes.fields}>
          <SelectField
            name="repairBumperPaymentType"
            label="Bumper Payment Type"
            options={BumperPaymentType}
            control={control}
            errors={errors}
            disabled={isEdit}
          />
          <SelectField
            name="repairPaymentType"
            label="Repair Payment Type"
            options={RepairPaymentType}
            control={control}
            errors={errors}
            disabled={isEdit}
          />
        </Box>

        <Box className={classes.fields}>
          <Controller
            name="active"
            control={control}
            render={({ field }) => (
              <BooleanInput
                label="Active"
                checked={field.value}
                onChange={field.onChange}
              />
            )}
          />
        </Box>
      </Box>

      <FormToolbar
        onGoBack={onGoBack}
        onSave={onFormSubmit}
        saveLoading={loading}
        showSave
      />
    </Box>
  );
};

const useStyles = makeStyles(theme => ({
  formContent: {
    padding: theme.spacing(2),
  },
  fields: {
    width: '100%',
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
    columnGap: theme.spacing(4),
    rowGap: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
}));

export default RepairsSettingsForm;
